import React from 'react';
import { Alert, StyleSheet, View } from 'react-native';
import { Button, Card, Text } from 'react-native-paper';
import ShareBadge from '../../../components/ShareBadge';
import { isDetoxTestControlsEnabled } from '../../../config/environments';
import { colors, spacing, typography } from '../../../constants/theme';
import { ReviewCategory, ReviewItem } from '../AdminDashboard.types';

interface AdminItemsSectionProps {
  categories: ReviewCategory[];
  items: ReviewItem[];
  onCreateItem: () => void;
  onDeactivateItem: (itemId: number) => void;
  onEditItem: (item: ReviewItem) => void;
  onOpenQuestions: (item: ReviewItem) => void;
  onRestoreItem: (itemId: number) => void;
  onShareItem: (item: ReviewItem) => void;
}

const getCategoryName = (categories: ReviewCategory[], categoryId: number) => {
  const category = categories.find(c => c.id === categoryId);
  return category?.name || 'Unknown Category';
};

const getDeactivatedFieldsCount = (item: ReviewItem) => {
  if (!item.questionsSchema?.fields) return 0;

  const fields = Array.isArray(item.questionsSchema.fields)
    ? item.questionsSchema.fields
    : [];

  return fields.filter((field) => field.deprecated).length;
};

const AdminItemsSection: React.FC<AdminItemsSectionProps> = ({
  categories,
  items,
  onCreateItem,
  onDeactivateItem,
  onEditItem,
  onOpenQuestions,
  onRestoreItem,
  onShareItem,
}) => {
  const renderItemCard = (item: ReviewItem) => {
    const deactivatedFieldsCount = getDeactivatedFieldsCount(item);

    return (
      <Card
        key={item.id}
        testID={`admin-item-${item.id}`}
        style={[styles.card, item.isDeprecated && styles.deactivatedCard]}
      >
        <Card.Content>
          <View style={styles.itemHeader}>
            <View style={styles.itemInfo}>
              <Text style={styles.itemName}>{item.name}</Text>
              <Text style={styles.categoryName}>{getCategoryName(categories, item.categoryId)}</Text>
              {item.description && (
                <Text style={styles.description}>{item.description}</Text>
              )}
              {item.isDeprecated && item.deprecatedAt && (
                <Text style={styles.deprecatedDate}>
                  Deactivated on {new Date(item.deprecatedAt).toLocaleDateString()}
                </Text>
              )}
              {item.sharedWith && item.sharedWith.length > 0 && (
                <ShareBadge
                  sharedWith={item.sharedWith}
                  onPress={(link) => {
                    Alert.alert(
                      'Shared Item',
                      `This item shares data with "${link.itemName}" in ${link.templateName || `Template ${link.templateId}`}\n\n${link.questionMappings?.length || 0} questions are mapped.`
                    );
                  }}
                />
              )}
            </View>
            <View style={styles.itemActions}>
              <Button
                testID={`admin-edit-item-${item.id}`}
                mode="outlined"
                onPress={() => onEditItem(item)}
                style={styles.editButton}
              >
                Edit
              </Button>
              <Button
                testID={`admin-share-item-${item.id}`}
                mode="outlined"
                onPress={() => onShareItem(item)}
                style={styles.shareButton}
                icon="share-variant"
              >
                Share
              </Button>
              {item.isDeprecated ? (
                <Button
                  mode="outlined"
                  onPress={() => onRestoreItem(item.id)}
                  style={styles.restoreButton}
                >
                  Restore
                </Button>
              ) : (
                <Button
                  mode="outlined"
                  onPress={() => onDeactivateItem(item.id)}
                  style={styles.deactivateButton}
                >
                  Deactivate
                </Button>
              )}
              <Button
                mode="text"
                onPress={() => onOpenQuestions(item)}
              >
                Questions
              </Button>
            </View>
          </View>

          <View style={styles.itemStats}>
            <Text style={styles.stat}>
              Questions: {Array.isArray(item.questionsSchema?.fields) ? item.questionsSchema.fields.length : 0}
            </Text>
            {deactivatedFieldsCount > 0 && (
              <Text style={styles.deactivatedStat}>
                Deactivated: {deactivatedFieldsCount}
              </Text>
            )}
          </View>
        </Card.Content>
      </Card>
    );
  };

  return (
    <>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Review Items ({items.length})</Text>
        <Button
          mode="contained"
          onPress={onCreateItem}
          style={styles.createButton}
        >
          + New Item
        </Button>
      </View>

      {isDetoxTestControlsEnabled && items[0] && (
        <View>
          <Button
            testID="admin-edit-first-item-button"
            mode="outlined"
            onPress={() => onEditItem(items[0])}
            style={styles.createButton}
          >
            Edit First Item
          </Button>
          <Button
            testID="admin-share-first-item-button"
            mode="outlined"
            onPress={() => onShareItem(items[0])}
            style={styles.createButton}
            icon="share-variant"
          >
            Share First Item
          </Button>
        </View>
      )}

      {items.length > 0 ? (
        items.map(renderItemCard)
      ) : (
        <Text style={styles.stat}>No items found</Text>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    ...typography.h2,
    marginBottom: spacing.md,
  },
  createButton: {},
  card: {
    marginBottom: spacing.md,
  },
  deactivatedCard: {
    backgroundColor: colors.surface,
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  itemName: {
    ...typography.h3,
    marginBottom: spacing.xs,
  },
  categoryName: {
    ...typography.caption,
    color: colors.primary,
    marginBottom: spacing.xs,
  },
  description: {
    ...typography.body,
    color: colors.textSecondary,
  },
  deprecatedDate: {
    ...typography.caption,
    color: colors.error,
    marginTop: spacing.xs,
    fontStyle: 'italic',
  },
  itemActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  editButton: {
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareButton: {
    borderColor: '#2196F3',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deactivateButton: {
    borderColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
  },
  restoreButton: {
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemStats: {
    flexDirection: 'row',
    marginTop: spacing.md,
    gap: spacing.md,
  },
  stat: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  deactivatedStat: {
    ...typography.caption,
    color: colors.error,
  },
});

export default AdminItemsSection;
